import {Tooltip} from '@mui/material'
import {Box, Button, Typography} from '@viptech/react-components'
import {endOfToday, startOfToday} from 'date-fns'
import moment from 'moment'
import {useEffect, useRef, useState} from 'react'
import CustomCalendar from 'src/components/customCalendar/CustomCalendar'
import useGetRetentionRange from 'src/hooks/api/streaming/useGetRetentionRange'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import ClockPlayer from 'src/images/ClockPlayer'
import rewind from 'src/images/rewind.svg'

type Props = {
  setOffset: (offset: number | undefined) => void
  offset: number | undefined
  playbackSpeeedRate: number
  setPlaybackSpeeedRate: (speed: number) => void
  cameraIds: Array<number>
}

function PlayerBar(props: Props) {
  const {styledTheme} = usePersonalizedThemeByCompany()
  const {setOffset, offset, setPlaybackSpeeedRate, playbackSpeeedRate, cameraIds} = props
  const ref = useRef<HTMLDivElement>(null)

  const [start, setStart] = useState<Date>(startOfToday())
  const [end, setEnd] = useState<Date>(endOfToday())

  const [selectedDate, setSelectDate] = useState<Date>(new Date())

  const {data: retentionRanges} = useGetRetentionRange({
    cameraIds,
    startDate: start.getTime() / 1000,
    endDate: Math.floor(end.getTime() / 1000),
    enabled: cameraIds.length > 0,
  })

  function handleClick(e: React.MouseEvent) {
    if (!ref.current) return

    const rect = ref.current.getBoundingClientRect()
    const percent = (e.clientX - rect.left) / rect.width
    const time = start.getTime() + percent * (end.getTime() - start.getTime())

    const selectedDate = new Date(time)
    const now = new Date()
    const offsetInSeconds = (now.getTime() - selectedDate.getTime()) / 1000
    setSelectDate(selectedDate)
    setOffset(Number(offsetInSeconds.toFixed(0)))
  }

  const cursorPercent = (selectedDate.getTime() - start.getTime()) / (end.getTime() - start.getTime())

  const [hoverDate, setHoverDate] = useState<Date | null>(null)
  const [hoverX, setHoverX] = useState(0)

  function positionToDate(clientX: number, rect: DOMRect, start: Date, end: Date) {
    const percent = (clientX - rect.left) / rect.width
    const clamped = Math.min(1, Math.max(0, percent))

    const time = start.getTime() + clamped * (end.getTime() - start.getTime())

    return new Date(time)
  }

  function timestampToPercent(tsSeconds: number) {
    const startMs = start.getTime()
    const endMs = end.getTime()
    const rangeMs = endMs - startMs
    if (rangeMs <= 0) return 0
    const tsMs = tsSeconds * 1000
    return (tsMs - startMs) / rangeMs
  }

  const [labelRewind, setLabelRewind] = useState(10)

  const [rangesGrouped, setRangesGrouped] = useState<{start: number; end: number}[]>([])

  useEffect(() => {
    if (!retentionRanges) return
    const ranges = groupContinuousTimestamps(retentionRanges.data.data ?? [])
    setRangesGrouped(ranges)
  }, [retentionRanges])

  function groupContinuousTimestamps(timestamps: number[]) {
    if (!timestamps.length) return []
    const gapSeconds = 1

    const ranges: {start: number; end: number}[] = []

    let rangeStart = timestamps[0]
    let prev = timestamps[0]

    for (let i = 1; i < timestamps.length; i++) {
      const current = timestamps[i]
      if (current - prev > gapSeconds) {
        ranges.push({start: rangeStart, end: prev})
        rangeStart = current
      }
      prev = current
    }
    ranges.push({start: rangeStart, end: prev})
    return ranges
  }

  return (
    <Box display='flex' direction='column' backgroundColor='#ffffff' border='1px solid #55565b' borderRadius='0 0 8px 8px' justifyContent='center'>
      <Box display='flex' color='white' align='center' columnGap='5px' borderBottom='1px  #55565B solid' padding='5px' justifyContent='center'>
        <Typography
          variant='p'
          size='10px'
          marginLeft='3px'
          children={moment(start).format('DD-MM-YY HH:mm')}
          style={{textWrap: 'nowrap'}}
          color={styledTheme.colors.text}
        />

        <Box
          width='100%'
          backgroundColor='#8D8989'
          height='12px'
          style={{cursor: 'pointer'}}
          ref={ref}
          onClick={handleClick}
          position='relative'
          borderRadius='8px'
          onMouseMove={(e) => {
            if (!ref.current) return
            const rect = ref.current.getBoundingClientRect()
            const date = positionToDate(e.clientX, rect, start, end)
            setHoverDate(date)
            setHoverX(e.clientX - rect.left)
          }}
          onMouseLeave={() => setHoverDate(null)}>
          {rangesGrouped.map((range, i) => {
            const left = timestampToPercent(range.start)
            const right = timestampToPercent(range.end)
            const clampedLeft = Math.max(0, left)
            const clampedRight = Math.min(1, right)
            const minWidthPx = 2

            const containerWidth = ref.current?.offsetWidth ?? 0
            const minPercent = containerWidth ? minWidthPx / containerWidth : 0.001

            const visualWidth = Math.max(clampedRight - clampedLeft, minPercent)
            if (right <= 0 || left >= 1) return null

            return (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: `${clampedLeft * 100}%`,
                  width: `${visualWidth * 100}%`,
                  height: '100%',
                  background: styledTheme.colors.primary,
                  opacity: 0.9,
                  borderRadius: '8px',
                }}
              />
            )
          })}

          <Tooltip title='Reproduzindo a partir daqui' placement='top'>
            <div
              style={{
                position: 'absolute',
                left: `${cursorPercent * 100}%`,
                top: 0,
                bottom: 0,
                width: 3,
                background: 'white',
                height: '12px',
              }}
            />
          </Tooltip>

          {hoverDate && (
            <div
              style={{
                position: 'absolute',
                left: hoverX,
                top: -28,
                transform: 'translateX(-50%)',
                padding: '4px 8px',
                fontSize: 11,
                background: '#111',
                color: '#fff',
                borderRadius: 4,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
                zIndex: 999,
              }}>
              Reproduzir a partir de: {hoverDate.toLocaleString('pt-br')}
            </div>
          )}
        </Box>
        <Typography
          variant='span'
          size='10px'
          children={moment(end).format('DD-MM-YY HH:mm')}
          style={{textWrap: 'nowrap', paddingRight: '2px'}}
          color={styledTheme.colors.text}
        />
      </Box>

      <Box padding='0 10px' display='flex' align='center' justifyContent='space-between' columnGap='10px'>
        <Box display='flex' align='center' columnGap='10px'>
          <Typography
            variant='p'
            size='12px'
            onClick={() => {
              setOffset(undefined)
              setSelectDate(new Date())
              setStart(startOfToday())
              setEnd(endOfToday())
              setLabelRewind(10)
            }}
            color={offset ? styledTheme.colors.text : styledTheme.colors.primary}
            weight={500}
            style={{cursor: 'pointer', textDecoration: 'underline'}}>
            {offset ? 'Ver Ao Vivo' : 'AO VIVO'}
          </Typography>

          <Tooltip title={`Voltar vídeo ${labelRewind} segundos`} placement='top'>
            <Button
              leftIcon={<img src={rewind} alt='voltar 10 s' style={{width: '18px'}} />}
              variant='contained'
              hoverColor='transparent'
              color='transparent'
              fontSize='12px'
              onClick={() => {
                setOffset(labelRewind)
                if (labelRewind <= 50) setLabelRewind(labelRewind + 10)
              }}>
              <Typography variant='p' children={`${labelRewind}s`} color={styledTheme.colors.text} />
            </Button>
          </Tooltip>
          <CustomCalendar
            initialDates={[start, end]}
            setDate={(dates) => {
              setStart(dates[0])
              setEnd(dates[1])
              setSelectDate(dates[0])
              const now = new Date()
              const offsetInSeconds = (now.getTime() - dates[0].getTime()) / 1000
              setOffset(Number(offsetInSeconds.toFixed(0)))
            }}
            hideRuler
            hideLabel
          />
        </Box>

        <Box display='flex' align='center' columnGap='10px'>
          <Tooltip title='Velocidade de reprodução'>
            <Box display='flex' align='center' columnGap='10px' border='1px solid #959597' padding='5px' borderRadius='8px'>
              <ClockPlayer />
              <Button variant='contained' hoverColor='transparent' color='transparent' fontSize='12px' padding='0' onClick={() => setPlaybackSpeeedRate(0.5)}>
                <Typography
                  variant='span'
                  children={`0.5`}
                  style={{minWidth: '25px'}}
                  weight={playbackSpeeedRate === 0.5 ? 700 : 500}
                  color={playbackSpeeedRate === 0.5 ? styledTheme.colors.primary : styledTheme.colors.text}
                />
              </Button>
              <Button variant='contained' hoverColor='transparent' color='transparent' fontSize='12px' padding='0' onClick={() => setPlaybackSpeeedRate(1)}>
                <Typography
                  variant='span'
                  children={`1x`}
                  style={{minWidth: '25px'}}
                  weight={playbackSpeeedRate === 1 ? 700 : 500}
                  color={playbackSpeeedRate === 1 ? styledTheme.colors.primary : styledTheme.colors.text}
                />
              </Button>
              <Button variant='contained' hoverColor='transparent' color='transparent' fontSize='12px' padding='0' onClick={() => setPlaybackSpeeedRate(2)}>
                <Typography
                  variant='span'
                  children={`2x`}
                  style={{minWidth: '25px'}}
                  weight={playbackSpeeedRate === 2 ? 700 : 500}
                  color={playbackSpeeedRate === 2 ? styledTheme.colors.primary : styledTheme.colors.text}
                />
              </Button>
              <Button variant='contained' hoverColor='transparent' color='transparent' fontSize='12px' padding='0' onClick={() => setPlaybackSpeeedRate(4)}>
                <Typography
                  variant='span'
                  children={`4x`}
                  style={{minWidth: '25px'}}
                  weight={playbackSpeeedRate === 4 ? 700 : 500}
                  color={playbackSpeeedRate === 4 ? styledTheme.colors.primary : styledTheme.colors.text}
                />
              </Button>
              <Button variant='contained' hoverColor='transparent' color='transparent' fontSize='12px' padding='0' onClick={() => setPlaybackSpeeedRate(8)}>
                <Typography
                  variant='span'
                  children={`8x`}
                  style={{minWidth: '25px'}}
                  weight={playbackSpeeedRate === 8 ? 700 : 500}
                  color={playbackSpeeedRate === 8 ? styledTheme.colors.primary : styledTheme.colors.text}
                />
              </Button>
            </Box>
          </Tooltip>
          {/* <Tooltip title='Baixar trecho de vídeo'>
            <Button variant='contained' hoverColor='transparent' color='transparent' fontSize='12px' padding='0' onClick={() => {}}>
              <DownloadIcon />
            </Button>
          </Tooltip> */}
        </Box>
      </Box>
    </Box>
  )
}

export default PlayerBar
